import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FilterFields } from "./FilterModal";
import { invalidateJobRoles } from "../../store/slices/findCandidatesSlice";

// ── chip display (read-only summary) ─────────────────────────────────────
const Chip = ({ label, variant = "filter" }) => {
    const base = "px-3 py-1.5 rounded-full text-sm font-medium";
    const styles = {
        filter: `${base} bg-gray-100 text-gray-700`,
        skill:  `${base} bg-purple-50 text-purple-700`,
        more:   `${base} bg-white text-gray-500 border border-gray-200`,
    };
    return <span className={styles[variant]}>{label}</span>;
};

// How the backend's `filled_from_job` field names read in the header sentence.
const FILLED_FROM_JOB_LABELS = {
    roles: "job title",
    locations: "location",
    experience: "experience",
    skills: "skills",
    skills_nice_to_have: "nice-to-have skills",
};

const headcountLabel = ({ company_headcount_min: min, company_headcount_max: max }) => {
    if (min != null && max != null) return `${min}–${max} employees`;
    if (max != null) return `up to ${max} employees`;
    if (min != null) return `${min}+ employees`;
    return null;
};

const buildSummaryChips = (cq) => {
    const chips = [];
    if (cq.roles?.length > 0) {
        chips.push({ label: cq.roles[0], variant: "filter" });
        if (cq.roles.length > 1)
            chips.push({ label: `+${cq.roles.length - 1} more titles`, variant: "more" });
    }
    cq.locations?.slice(0, 2).forEach(loc => chips.push({ label: `in ${loc}`, variant: "filter" }));
    if ((cq.locations?.length ?? 0) > 2)
        chips.push({ label: `+${cq.locations.length - 2} more locations`, variant: "more" });
    if (cq.seniority) chips.push({ label: cq.seniority, variant: "filter" });
    if (cq.experience_min != null || cq.experience_max != null) {
        const min = cq.experience_min ?? 0;
        chips.push({ label: cq.experience_max != null ? `${min}–${cq.experience_max} yrs exp` : `${min}+ yrs exp`, variant: "filter" });
    }
    cq.companies?.slice(0, 2).forEach(c => chips.push({ label: `@ ${c}`, variant: "filter" }));
    if ((cq.companies?.length ?? 0) > 2)
        chips.push({ label: `+${cq.companies.length - 2} companies`, variant: "more" });
    const sectors = [...(cq.industries ?? []), ...(cq.company_keywords ?? [])];
    if (sectors.length > 0) chips.push({ label: sectors.slice(0, 2).join(" / "), variant: "filter" });
    const size = headcountLabel(cq);
    if (size) chips.push({ label: size, variant: "filter" });
    const required = cq.skills_required ?? [];
    if (cq.skills_min_match && cq.skills_min_match < required.length)
        chips.push({ label: `at least ${cq.skills_min_match} of these skills`, variant: "more" });
    required.slice(0, 4).forEach(s => chips.push({ label: s, variant: "skill" }));
    if (required.length > 4)
        chips.push({ label: `+${required.length - 4} skills`, variant: "more" });
    if ((cq.skills_nice_to_have?.length ?? 0) > 0)
        chips.push({ label: `+${cq.skills_nice_to_have.length} nice-to-have`, variant: "more" });
    return chips;
};

// ── main component ────────────────────────────────────────────────────────
const SourcingPreview = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const query = location.state?.query || "";
    const jobId = location.state?.job_id || null;
    const jobRole = location.state?.job_role || null;

    const portal = location.pathname.startsWith("/hr") ? "hr" : "org-admin";

    const [parsedQuery, setParsedQuery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const [noResultsHint, setNoResultsHint] = useState(null);
    // Set once a run has found nobody. The search record exists by then, so the next run
    // updates it rather than leaving an empty search behind.
    const [searchId, setSearchId] = useState(null);

    useEffect(() => {
        if (!query) { navigate(`/${portal}/sourcing`); return; }
        // job_id matters as much as the query here: the backend reads the JD behind it, so
        // a loose line ("exp 2-3 years") is filled in from the role being hired for rather
        // than rejected as unparseable.
        axios
            .post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/parse`, { query, job_id: jobId })
            .then(({ data }) => setParsedQuery(data.canonical_query))
            .catch(() => setError("Couldn't parse your query. Please try again."))
            .finally(() => setLoading(false));
        // Parse once for the query this page was opened with — re-running it would
        // discard whatever the recruiter has edited in the filters below.
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Run the search here rather than on a results page, then hand off to Find Candidates
    // with the job already scoped — the new candidates are part of that job's sourced pool
    // the moment /sourcing/find persists them.
    const handleRunSearch = async () => {
        if (searching) return;
        setSearching(true);
        setError(null);
        setNoResultsHint(null);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_SOURCING_API_URL}/sourcing/find`, {
                query,
                organisation_id: localStorage.getItem("current_org_id"),
                recruiter_id: localStorage.getItem("org_admin_id"),
                canonical_query: parsedQuery,
                job_id: jobId,
                job_role: jobRole,
                search_id: searchId,
            });
            // Nobody matched every filter: stay on the filters, open, with the reason —
            // rather than landing on an empty list. The filters shown become the ones
            // actually searched, since the job's skills may have been loosened.
            if (data?.no_results_hint && !data?.total) {
                setSearchId(data.search_id || searchId);
                if (data.parsed_query) setParsedQuery(data.parsed_query);
                setNoResultsHint(data.no_results_hint);
                setEditOpen(true);
                setSearching(false);
                return;
            }
            // This role's Sourced count just grew and it's now the latest sourced.
            dispatch(invalidateJobRoles());
            // Straight to the job's pipeline, skipping the role picker — the recruiter
            // already named the role when they set up this search. Scoped to the search
            // just run, not the job's whole pool: these are the candidates they came for,
            // and it puts the search in hand for Edit Filters.
            navigate(`/${portal}/find-candidates/${jobId}`, {
                state: {
                    job_id: jobId,
                    job_role: jobRole,
                    search_id: data?.search_id,
                    // Fewer than a page came back: the next screen says what a looser filter
                    // would have found, so the number isn't a mystery.
                    search_notice: data?.notice?.message || null,
                },
            });
        } catch (err) {
            console.error(err.response?.data);
            setError("Search failed. Please try again.");
            setSearching(false);
        }
    };

    const filledFromJob = (parsedQuery?.filled_from_job ?? []).map(f => FILLED_FROM_JOB_LABELS[f] || f);

    return (
        <div className="min-h-screen bg-bgPrimary dark:bg-none flex flex-col items-center justify-center px-6">
            <div className="w-full max-w-2xl">

                {/* query pill */}
                <div className="mb-6 px-5 py-3 rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-white/5 text-sm text-gray-600 dark:text-gray-300 truncate">
                    {query}
                </div>

                {loading && (
                    <div className="flex flex-col items-center py-16 gap-3">
                        <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-gray-400 dark:text-gray-500">Understanding your query...</p>
                    </div>
                )}

                {error && (
                    <p className="text-sm text-red-400 text-center py-8">{error}</p>
                )}

                {parsedQuery && !loading && (
                    <>
                        {noResultsHint && (
                            <div className="mb-4 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                                {noResultsHint.message}
                            </div>
                        )}

                        {/* filter card */}
                        <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-gray-700/50 shadow-sm mb-4 overflow-hidden">

                            {/* header row */}
                            <div className="flex items-center justify-between px-5 pt-5 pb-3">
                                {/* Names the JD as a source only for the filters actually taken
                                    from it. A job for a different role contributes nothing, and
                                    crediting it anyway would read as if it had. */}
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    I've set these{" "}
                                    <span className="text-purple-600 dark:text-purple-300 font-medium">filters</span>{" "}
                                    based on what you're looking for
                                    {jobRole && filledFromJob.length > 0 && (
                                        <> and the <span className="font-medium">{jobRole}</span> job description ({filledFromJob.join(", ")})</>
                                    )}
                                </p>
                                <button
                                    onClick={() => setEditOpen(v => !v)}
                                    className="text-xs text-purple-600 dark:text-purple-300 hover:text-purple-800 font-medium shrink-0 ml-4"
                                >
                                    {editOpen ? "Done" : "Edit filters"}
                                </button>
                            </div>

                            {/* summary chips — visible when not editing */}
                            {!editOpen && (
                                <div className="flex flex-wrap gap-2 px-5 pb-5">
                                    {buildSummaryChips(parsedQuery).map((chip, i) => (
                                        <Chip key={i} label={chip.label} variant={chip.variant} />
                                    ))}
                                </div>
                            )}

                            {/* edit form — visible when editing */}
                            {editOpen && (
                                <div className="px-5 pb-5 flex flex-col gap-4 border-t border-gray-50 pt-4">
                                    <FilterFields query={parsedQuery} onChange={setParsedQuery} />
                                </div>
                            )}
                        </div>

                        {/* actions */}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => navigate(`/${portal}/sourcing`)}
                                disabled={searching}
                                className="px-5 py-2.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 transition-all"
                            >
                                Reset Search
                            </button>
                            <button
                                onClick={handleRunSearch}
                                disabled={searching}
                                className="px-6 py-2.5 rounded-full text-sm font-medium text-white disabled:opacity-70 transition-all flex items-center gap-2"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                {searching && (
                                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                )}
                                {searching ? "Searching…" : "Run Search"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SourcingPreview;
